import React, { MutableRefObject, ReactNode, createContext, useCallback, useContext, useRef, useState } from 'react';
import { FlatList } from 'react-native';
import { SharedValue, useSharedValue } from 'react-native-reanimated';

import { PlayerInfoWithProjectionsFragment } from '@/api/events/query.generated';

export interface PlayerFilters {
    listRef: MutableRefObject<FlatList<any> | null>;
    listScrollOffset: SharedValue<number>;
    filtersStickPosition: SharedValue<number>;
    playerProjectionFilter: string | undefined;
    playerProjectionLabel: string | undefined;
    playerPositionFilter: string | undefined;
    playerPositionDescription: string | undefined;
    setPlayerProjectionFilter: (value?: string, label?: string) => void;
    resetProjectionsFilter: () => void;
    setPlayerPositionFilter: (value: string, description: string) => void;
    resetPositionFilter: () => void;
    resetAllFilters: () => void;
    scrollToFilters: () => void;
}

const PlayerFiltersContext = createContext<PlayerFilters | undefined>(undefined);

type PlayerFiltersState = {
    playerProjectionFilter: string | undefined;
    playerProjectionLabel: string | undefined;
    playerPositionFilter: string | undefined;
    playerPositionDescription: string | undefined;
};

interface PlayerFiltersProviderProps {
    children: ReactNode;
}

export const PlayerFiltersProvider: React.FC<PlayerFiltersProviderProps> = ({ children }) => {
    const listRef = useRef<FlatList | null>(null);
    const filtersStickPosition = useSharedValue(0);
    const listScrollOffset = useSharedValue(0);
    // Use a single state object
    const [state, setState] = useState<PlayerFiltersState>({
        playerProjectionFilter: undefined,
        playerProjectionLabel: undefined,
        playerPositionFilter: undefined,
        playerPositionDescription: undefined,
    });

    const setPlayerProjectionFilter = useCallback((value?: string, label?: string) => {
        setState(prevState => ({
            ...prevState,
            playerProjectionFilter: value,
            playerProjectionLabel: label,
        }));
    }, []);

    const resetProjectionsFilter = useCallback(() => {
        setState(prevState => ({
            ...prevState,
            playerProjectionFilter: undefined,
            playerProjectionLabel: undefined,
        }));
    }, []);

    const setPlayerPositionFilter = useCallback((value: string, description: string) => {
        setState(prevState => ({
            ...prevState,
            playerPositionFilter: value,
            playerPositionDescription: description,
        }));
    }, []);

    const resetPositionFilter = useCallback(() => {
        setState(prevState => ({
            ...prevState,
            playerPositionFilter: undefined,
            playerPositionDescription: undefined,
        }));
    }, []);

    const resetAllFilters = useCallback(() => {
        setState({
            playerProjectionFilter: undefined,
            playerProjectionLabel: undefined,
            playerPositionFilter: undefined,
            playerPositionDescription: undefined,
        });
    }, []);

    const scrollToFilters = useCallback(() => {
        if (listScrollOffset.value > filtersStickPosition.value) {
            listRef?.current?.scrollToOffset({
                animated: false,
                offset: filtersStickPosition.value + 1, // + 1 to make sure the header displays the game time on event page
            });
        }
    }, [filtersStickPosition.value, listScrollOffset.value]);

    const value: PlayerFilters = {
        listRef,
        filtersStickPosition,
        listScrollOffset,
        ...state,
        setPlayerProjectionFilter,
        resetProjectionsFilter,
        setPlayerPositionFilter,
        resetPositionFilter,
        resetAllFilters,
        scrollToFilters,
    };

    return <PlayerFiltersContext.Provider value={value}>{children}</PlayerFiltersContext.Provider>;
};

export const usePlayerFilters = (): PlayerFilters => {
    const context = useContext(PlayerFiltersContext);
    if (context === undefined) {
        throw new Error('usePlayerFilters must be used within a PlayerFiltersProvider');
    }
    return context;
};

const specialPositionFilter = (playerPositionFilter: string, playerPosition: string) => {
    return playerPositionFilter === 'P' && (playerPosition === 'RP' || playerPosition === 'SP');
};

export const filterByProjectionFunction =
    (playerProjectionFilter: string | undefined) => (player: PlayerInfoWithProjectionsFragment) => {
        return (
            !playerProjectionFilter || player.projections?.some(projection => projection.key === playerProjectionFilter)
        );
    };

export const filterByPositionFunction =
    (playerPositionFilter: string | undefined) => (player: PlayerInfoWithProjectionsFragment) => {
        return (
            !playerPositionFilter ||
            player.position === playerPositionFilter ||
            specialPositionFilter(playerPositionFilter, player.position)
        );
    };
